import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { formatBRL } from '../utils/format';
import { Trash2, Plus, Minus, ArrowRight, ArrowLeft, ShoppingBag, ShieldCheck } from 'lucide-react';

export function CartPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { items, subtotalCents, updateQuantity, removeItem } = useCart();
  const { settings } = useStore();

  const freeThreshold = settings.shipping_free_threshold_cents || 45000;
  const remainingForFree = Math.max(0, freeThreshold - subtotalCents);
  const progressPercent = Math.min(100, Math.round((subtotalCents / freeThreshold) * 100));

  if (items.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-surface-muted mx-auto flex items-center justify-center text-ink-muted">
          <ShoppingBag className="w-7 h-7 stroke-1" />
        </div>
        <h1 className="text-2xl font-serif font-medium text-ink">Sua sacola está vazia</h1>
        <p className="text-xs text-ink-muted leading-relaxed max-w-sm mx-auto">
          Explore nossas cerâmicas autorais, peças têxteis em linho lavado e arquivos digitais exclusivos.
        </p>
        <button
          onClick={() => onNavigate('/catalogo')}
          className="mt-2 bg-ink text-ink-inverted px-6 py-3 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
        >
          Explorar Catálogo
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border-subtle pb-6">
        <div>
          <button
            onClick={() => onNavigate('/catalogo')}
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-2 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Continuar Comprando
          </button>
          <h1 className="text-3xl font-serif font-normal text-ink">Sua Sacola de Compras</h1>
        </div>
        <span className="text-xs text-ink-muted">
          {items.length} {items.length === 1 ? 'item' : 'itens'}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left: Items List */}
        <div className="lg:col-span-8 space-y-6">
          {/* Free Shipping Alert */}
          <div className="bg-surface p-4 rounded-xs border border-border-subtle space-y-2">
            <div className="flex items-center justify-between text-xs">
              {remainingForFree > 0 ? (
                <span className="text-ink-muted">
                  Adicione mais <strong className="text-ink">{formatBRL(remainingForFree)}</strong> para obter <strong className="text-primary">Frete Cortesia</strong>
                </span>
              ) : (
                <span className="font-medium text-olive flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4" /> Você conquistou Frete Cortesia para todo o Brasil!
                </span>
              )}
              <span className="font-mono text-xs text-ink-muted">{progressPercent}%</span>
            </div>
            <div className="w-full bg-border-subtle h-2 rounded-full overflow-hidden">
              <div
                className="bg-primary h-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="divide-y divide-border-subtle border-y border-border-subtle">
            {items.map((item) => (
              <div key={item.cart_item_id} className="py-6 flex gap-6 items-start">
                <img
                  src={item.cover_image || 'https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?auto=format&fit=crop&w=300&q=80'}
                  alt={item.product_title}
                  className="w-24 h-28 object-cover rounded-xs bg-background-alt shrink-0"
                />

                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3
                        onClick={() => onNavigate(`/produto/${item.product_slug}`)}
                        className="font-serif text-lg font-medium text-ink hover:text-primary cursor-pointer transition-colors line-clamp-1"
                      >
                        {item.product_title}
                      </h3>
                      <p className="text-xs text-ink-muted mt-0.5">{item.variant_title}</p>
                      <p className="text-xs font-mono text-ink-muted mt-1">SKU: {item.sku}</p>
                    </div>
                    <span className="font-mono text-base font-medium text-ink">
                      {formatBRL(item.price_cents * item.quantity)}
                    </span>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    {/* Quantity Controls */}
                    <div className="flex items-center border border-border-subtle rounded-xs bg-surface">
                      <button
                        onClick={() => updateQuantity(item.cart_item_id, item.quantity - 1)}
                        className="px-2.5 py-1.5 text-ink-muted hover:text-ink disabled:opacity-30"
                        aria-label="Diminuir quantidade"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="px-3 text-xs font-mono font-medium min-w-[28px] text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.cart_item_id, item.quantity + 1)}
                        className="px-2.5 py-1.5 text-ink-muted hover:text-ink"
                        aria-label="Aumentar quantidade"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <button
                      onClick={() => removeItem(item.cart_item_id)}
                      className="text-xs text-ink-muted hover:text-red-600 transition-colors flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Remover
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Order Summary */}
        <div className="lg:col-span-4">
          <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4 sticky top-28">
            <h2 className="font-serif text-lg font-medium text-ink border-b border-border-subtle pb-3">
              Resumo da Compra
            </h2>

            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between text-ink-muted">
                <span>Subtotal dos Produtos</span>
                <span className="font-mono font-medium text-ink">{formatBRL(subtotalCents)}</span>
              </div>
              <div className="flex justify-between text-ink-muted">
                <span>Frete Estimado</span>
                <span className="font-mono">
                  {subtotalCents >= freeThreshold ? (
                    <span className="text-olive font-medium">Grátis</span>
                  ) : (
                    formatBRL(settings.shipping_flat_rate_cents || 2800)
                  )}
                </span>
              </div>
            </div>

            <div className="pt-3 border-t border-border-subtle flex justify-between items-baseline">
              <span className="text-sm font-medium text-ink">Total Estimado</span>
              <span className="font-mono text-xl font-medium text-ink">
                {formatBRL(subtotalCents + (subtotalCents >= freeThreshold ? 0 : (settings.shipping_flat_rate_cents || 2800)))}
              </span>
            </div>

            <button
              onClick={() => onNavigate('/checkout')}
              className="w-full bg-ink text-ink-inverted py-3.5 px-4 rounded-xs text-xs uppercase tracking-widest font-medium hover:bg-primary transition-colors flex items-center justify-center gap-2 group shadow-subtle mt-4"
            >
              Ir para o Checkout
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>

            <div className="pt-2 text-[11px] text-ink-muted space-y-1.5 border-t border-border-subtle">
              <div className="flex items-center gap-1.5 text-olive font-medium">
                <ShieldCheck className="w-3.5 h-3.5" />
                Compra segura e dados criptografados
              </div>
              <p>Envio rastreado para todo o território nacional.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
